type CrispCommand = any[];

const CRISP_SCRIPT_SRC = 'https://client.crisp.chat/l.js';
// Keeping current production ID as fallback so we don't accidentally break chat if env isn't set.
const FALLBACK_CRISP_WEBSITE_ID = '3666006c-6e66-4eec-90ce-7cb6f4489b3f';

function isCrispDebugEnabled() {
  if (typeof process !== 'undefined' && process?.env?.REACT_APP_DEBUG_CRISP === 'true') return true;
  // Optional runtime flag from DevTools: window.__DEBUG_CRISP = true
  if (typeof window !== 'undefined' && (window as any).__DEBUG_CRISP === true) return true;
  return false;
}

function ensureQueue() {
  if (typeof window === 'undefined') return;
  window.$crisp = window.$crisp || [];
}

function disableCrispAvailabilityTooltip() {
  crispPush(['config', 'availability:tooltip', [false]]);
}

function walkShadowHosts(root: ParentNode, visit: (el: Element) => void) {
  const walk = (node: ParentNode) => {
    if (node instanceof Element) {
      visit(node);
      if (node.shadowRoot) walk(node.shadowRoot);
      Array.from(node.children).forEach((child) => walk(child));
      return;
    }
    if (node instanceof DocumentFragment) {
      Array.from(node.children).forEach((child) => walk(child));
    }
  };
  walk(root);
}

function concealCrispGeneralEntice() {
  if (typeof document === 'undefined') return;

  const conceal = (root: ParentNode) => {
    root.querySelectorAll?.('[data-id="general_entice"]').forEach((el) => {
      el.setAttribute('data-is-concealed', 'true');
      (el as HTMLElement).style.setProperty('display', 'none', 'important');
    });
  };

  conceal(document.body);
  walkShadowHosts(document.body, (el) => {
    if (el.shadowRoot) conceal(el.shadowRoot);
  });
}

let crispEnticeConcealRegistered = false;

function scheduleCrispEnticeConceal() {
  disableCrispAvailabilityTooltip();
  concealCrispGeneralEntice();
  [0, 300, 1000, 2500].forEach((delay) => {
    window.setTimeout(concealCrispGeneralEntice, delay);
  });
}

function ensureCrispEnticeConcealHandlers() {
  if (crispEnticeConcealRegistered) return;
  crispEnticeConcealRegistered = true;
  crispOn('session:loaded', scheduleCrispEnticeConceal);
  crispOn('website:availability:changed', scheduleCrispEnticeConceal);
}

function toCrispSessionKey(key: string) {
  // Crisp session:data keys should be lowercase. Allowed special chars: "_" and "-".
  // Convert camelCase/PascalCase to snake_case, then lowercase and sanitize.
  const snake = key
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s.]+/g, '_')
    .toLowerCase();
  return snake.replace(/[^a-z0-9_-]/g, '_');
}

export function loadCrisp(options?: { websiteId?: string }) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  ensureQueue();
  ensureCrispEnticeConcealHandlers();
  scheduleCrispEnticeConceal();

  const websiteId =
    options?.websiteId ||
    process.env.REACT_APP_CRISP_WEBSITE_ID ||
    window.CRISP_WEBSITE_ID ||
    FALLBACK_CRISP_WEBSITE_ID;

  window.CRISP_WEBSITE_ID = websiteId;

  // Avoid injecting the script multiple times.
  const existing = document.querySelector(`script[src="${CRISP_SCRIPT_SRC}"]`);
  if (existing) {
    if (isCrispDebugEnabled()) console.debug('[crisp] load: script already present', { websiteId });
    return;
  }

  const s = document.createElement('script');
  s.src = CRISP_SCRIPT_SRC;
  s.async = true;
  document.head.appendChild(s);
  if (isCrispDebugEnabled()) console.debug('[crisp] load: injected script', { websiteId });
}

export function crispPush(command: CrispCommand) {
  if (typeof window === 'undefined') return;
  ensureQueue();
  window.$crisp!.push(command);
  if (isCrispDebugEnabled()) console.debug('[crisp] push', command);
}

export function crispSetSessionData(data: Record<string, any>) {
  if (!data) return;
  Object.entries(data).forEach(([rawKey, rawValue]) => {
    if (rawValue === undefined) return;
    const key = toCrispSessionKey(rawKey);
    // Crisp typically expects primitives; stringify objects to avoid silent drops.
    const value =
      rawValue === null || typeof rawValue === 'string' || typeof rawValue === 'number' || typeof rawValue === 'boolean'
        ? rawValue
        : JSON.stringify(rawValue);

    // Crisp docs: ["set", "session:data", ["order_id", "3535353214"]]
    crispPush(['set', 'session:data', [key, value]]);
    if (isCrispDebugEnabled()) console.debug('[crisp] session:data', { key, value });
  });
}

export function crispIdentify(user: {
  email?: string;
  name?: string;
  id?: string;
  data?: Record<string, any>;
}) {
  if (!user) return;

  if (user.email) crispPush(['set', 'user:email', user.email]);
  if (user.name) crispPush(['set', 'user:nickname', user.name]);

  const sessionData: Record<string, any> = {
    ...(user.id ? { user_id: user.id } : {}),
    ...(user.data || {}),
  };

  crispSetSessionData(sessionData);
}

export function crispReset() {
  crispPush(['do', 'session:reset']);
}

export function crispShow() {
  crispPush(['do', 'chat:show']);
  scheduleCrispEnticeConceal();
}

export function crispHide() {
  crispPush(['do', 'chat:hide']);
}

export function crispOpen() {
  crispPush(['do', 'chat:open']);
}

export function crispOn(event: string, cb: (...args: any[]) => void) {
  crispPush(['on', event, cb]);
}


